import { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { Check, Clock, Megaphone, Wallet } from 'lucide-react';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { SearchInput } from '@/components/SearchInput';
import type { AvailableAdAccount } from '@/services/meta-oauth.service';

type Tone = 'success' | 'warning' | 'danger' | 'neutral';

// Meta's account_status enum. Disabled and closed accounts can't run ads, so they aren't offered.
const STATUS: Record<number, { label: string; tone: Tone; selectable: boolean }> = {
  1: { label: 'Active', tone: 'success', selectable: true },
  2: { label: 'Disabled', tone: 'danger', selectable: false },
  3: { label: 'Payment due', tone: 'warning', selectable: true },
  7: { label: 'Under review', tone: 'warning', selectable: true },
  8: { label: 'Pending settlement', tone: 'warning', selectable: true },
  9: { label: 'Grace period', tone: 'warning', selectable: true },
  100: { label: 'Closing', tone: 'neutral', selectable: true },
  101: { label: 'Closed', tone: 'neutral', selectable: false },
  201: { label: 'Active', tone: 'success', selectable: true },
  202: { label: 'Closed', tone: 'neutral', selectable: false },
};
const statusOf = (code: number) => STATUS[code] ?? { label: 'Unknown status', tone: 'neutral' as Tone, selectable: true };

const TONE_CLASS: Record<Tone, string> = {
  success: 'bg-success-soft text-success',
  warning: 'bg-warning-soft text-warning',
  danger: 'bg-danger-soft text-danger',
  neutral: 'bg-surface-alt text-text-muted',
};

const initials = (name: string) => name.split(/\s+/).filter(w => /[A-Za-z0-9]/.test(w)).slice(0, 2).map(w => w[0]).join('').toUpperCase() || 'AD';
const city = (tz: string) => tz.split('/').pop()?.replace(/_/g, ' ') || tz;

interface Props {
  open: boolean;
  accounts: AvailableAdAccount[];
  busy: boolean;
  onCancel: () => void;
  onConnect: (adAccountId: string) => void;
}

export function MetaAdAccountChooser({ open, accounts, busy, onCancel, onConnect }: Props) {
  const [query, setQuery] = useState('');
  const sorted = useMemo(() => [...accounts].sort((a, b) => Number(statusOf(b.status).selectable) - Number(statusOf(a.status).selectable)), [accounts]);
  const [selected, setSelected] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setSelected(sorted.find(a => statusOf(a.status).selectable)?.id ?? null);
  }, [open, sorted]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? sorted.filter(a => `${a.name} ${a.id} ${a.currency}`.toLowerCase().includes(q)) : sorted;
  }, [sorted, query]);

  return (
    <Modal open={open} onClose={onCancel} title="Choose an ad account" widthClass="max-w-xl">
      <div className="mb-4 flex items-start gap-3 rounded-xl border border-border bg-surface-alt/60 px-3.5 py-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#1877F2]/10 text-[#1877F2]">
          <Megaphone size={17} />
        </span>
        <div className="min-w-0">
          <div className="text-sm font-semibold text-text-primary">Signed in with Meta</div>
          <p className="mt-0.5 text-xs leading-snug text-text-muted">
            Your login can manage {accounts.length} ad account{accounts.length === 1 ? '' : 's'}. Pick the one this workspace uses for campaigns, audiences and lead ads.
          </p>
        </div>
      </div>

      {accounts.length > 6 && (
        <div className="mb-3">
          <SearchInput value={query} onChange={setQuery} placeholder="Search by name, ID or currency…" />
        </div>
      )}

      <div role="radiogroup" aria-label="Ad accounts" className="-mx-1 max-h-[min(360px,42vh)] space-y-2 overflow-y-auto px-1 py-0.5">
        {visible.map((acc) => {
          const status = statusOf(acc.status);
          const isSelected = selected === acc.id;
          return (
            <button
              key={acc.id}
              type="button"
              role="radio"
              aria-checked={isSelected}
              disabled={busy || !status.selectable}
              onClick={() => setSelected(acc.id)}
              onDoubleClick={() => status.selectable && !busy && onConnect(acc.id)}
              title={status.selectable ? undefined : `This ad account is ${status.label.toLowerCase()} in Meta and can't run ads.`}
              style={{ outline: 'none' }}
              className={clsx(
                'flex w-full items-center gap-3 rounded-xl border px-3.5 py-3 text-left transition-all duration-150',
                isSelected
                  ? 'border-primary bg-primary-soft/40 ring-4 ring-primary-soft'
                  : 'border-border bg-surface hover:border-border-strong hover:bg-surface-alt/50',
                !status.selectable && 'cursor-not-allowed opacity-55',
              )}
            >
              <span className={clsx(
                'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-sm font-bold',
                isSelected ? 'bg-gold-gradient text-on-primary' : 'bg-gold-gradient-soft text-primary',
              )}>
                {initials(acc.name)}
              </span>

              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold text-text-primary">{acc.name || acc.id}</span>
                <span className="mt-0.5 block font-mono text-2xs text-text-muted">ID {acc.id.replace(/^act_/, '')}</span>
                <span className="mt-1.5 flex flex-wrap items-center gap-1.5">
                  {acc.currency && (
                    <span className="inline-flex items-center gap-1 rounded-md bg-surface-alt px-1.5 py-0.5 text-2xs font-medium text-text-secondary">
                      <Wallet size={11} /> {acc.currency}
                    </span>
                  )}
                  {acc.timezone && (
                    <span className="inline-flex items-center gap-1 rounded-md bg-surface-alt px-1.5 py-0.5 text-2xs font-medium text-text-secondary" title={acc.timezone}>
                      <Clock size={11} /> {city(acc.timezone)}
                    </span>
                  )}
                </span>
              </span>

              <span className="flex shrink-0 items-center gap-3">
                <span className={clsx('rounded-full px-2 py-0.5 text-2xs font-semibold', TONE_CLASS[status.tone])}>{status.label}</span>
                <span className={clsx(
                  'flex h-5 w-5 items-center justify-center rounded-full border-2 transition-colors',
                  isSelected ? 'border-primary bg-primary text-on-primary' : 'border-border-strong',
                )}>
                  {isSelected && <Check size={12} strokeWidth={3} />}
                </span>
              </span>
            </button>
          );
        })}
        {visible.length === 0 && <p className="py-6 text-center text-sm text-text-muted">No ad account matches “{query}”.</p>}
      </div>

      <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4">
        <p className="text-2xs text-text-muted">You can connect more ad accounts later from Ad Accounts.</p>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={onCancel} disabled={busy}>Cancel</Button>
          <Button loading={busy} disabled={!selected} onClick={() => selected && onConnect(selected)}>Connect account</Button>
        </div>
      </div>
    </Modal>
  );
}
